import type { ScheduleVoyage } from "../../shared/api/types";

// Pure merge for upserting voyages into the keyed-by-id voyages map.
//
// Dedup key is (vesselCode, voyageNo, portName) — the natural identity
// of a single terminal call. Drop any existing entries that share this
// identity with an incoming voyage before adding, so:
//
//   - A patch-fetched bar cleanly replaces a synthetic placeholder the
//     hidden-stop reveal previously pushed (different voyageReference
//     / arrivalLocal → different id, so without the dedup the synthetic
//     and the real entry would coexist as two bars at the same call).
//   - A re-fetched bar whose arrivalLocal drifted between fetches
//     replaces the old entry instead of stacking next to it.
//
// Map keys mirror voyageItemId() in voyage-mapper.ts so vis-timeline's
// items DataSet keeps a 1:1 with this map. Returns the SAME reference
// when `incoming` is empty so Solid's setStore(same-ref) short-circuits
// the reactive emit (identity-on-no-change contract — see CLAUDE.md).
export function mergeUpsertVoyages(
  current: Readonly<Record<string, ScheduleVoyage>>,
  incoming: readonly ScheduleVoyage[],
): Record<string, ScheduleVoyage> {
  if (incoming.length === 0) return current as Record<string, ScheduleVoyage>;
  const next = { ...current };
  const incomingKeys = new Set<string>();
  for (const v of incoming) {
    incomingKeys.add(`${v.vesselCode}|${v.voyageNo}|${v.portName}`);
  }
  for (const [id, v] of Object.entries(next)) {
    const k = `${v.vesselCode}|${v.voyageNo}|${v.portName}`;
    if (incomingKeys.has(k)) delete next[id];
  }
  for (const v of incoming) {
    const id = `${v.voyageReference}|${v.voyageNo}|${v.portName}|${v.arrivalLocal}`;
    next[id] = v;
  }
  return next;
}
